import React, { useEffect, useState } from 'react';
import {
  Modal,
  Steps,
  Button,
  Row,
  Col,
  Alert,
  Descriptions,
  Space,
  Typography,
  Spin,
  Switch,
  Select,
  Divider,
  Tag,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import SelectField from 'components/Forms/select';
import InputField from 'components/Forms/input';
import { useMarketplaces } from 'services/MarketplaceService';
import { useStoresByMarketplace } from 'services/StoreService';
import { useDrivers } from 'services/DriverService';
import { useWarehouse } from 'services/WarehouseService';
import {
  useCreateCollection,
  useDriverCollectionInfo,
  CreateCollectionPayload,
} from 'services/CollectionsService';
import { Paquer } from 'interfaces/paquer';
import Card from 'components/card';
import defaultMarkerIcon from '../collections/mapMarkerIcon';

const { Text } = Typography;

export interface StoreEntry {
  marketplaceId: number;
  marketplaceName: string;
  storeId: number;
  storeName: string;
  address: string;
  lat: number;
  lng: number;
}

export interface DepositInfo {
  name: string;
  address: string;
  lat: number;
  lng: number;
}

export interface CollectionFormValues {
  stores: StoreEntry[];
  driverId: number | null;
  driverName: string;
  driverDni: string;
  vehiclePlate: string;
  notifyStore: boolean;
  notifyDriver: boolean;
  deposit: DepositInfo | null;
}

const initialValues: CollectionFormValues = {
  stores: [],
  driverId: null,
  driverName: '',
  driverDni: '',
  vehiclePlate: '',
  notifyStore: true,
  notifyDriver: true,
  deposit: null,
};

const validationSchema = Yup.object({
  stores: Yup.array().min(1, 'Agregá al menos una tienda'),
  driverId: Yup.number().nullable().required('Seleccioná un driver'),
  vehiclePlate: Yup.string().trim().required('Ingresá el dominio del vehículo'),
});

const STEP_FIELDS: (keyof CollectionFormValues)[][] = [
  ['driverId', 'vehiclePlate'],
  ['stores'],
  [],
];

interface FormContentProps {
  currentStep: number;
  onNext: () => void;
  onBack: () => void;
  onClose: () => void;
}

const FormContent: React.FC<FormContentProps> = ({
  currentStep,
  onNext,
  onBack,
  onClose,
}) => {
  const {
    values,
    setFieldValue,
    validateForm,
    setFieldTouched,
    isSubmitting,
    submitForm,
    touched,
    errors,
  } = useFormikContext<CollectionFormValues>();

  const [stagingMarketplaceId, setStagingMarketplaceId] = useState<
    number | null
  >(null);
  const [stagingStoreId, setStagingStoreId] = useState<number | null>(null);

  const { data: marketplaces = [], isLoading: marketplacesLoading } =
    useMarketplaces();
  const { data: stagingStores = [], isLoading: stagingStoresLoading } =
    useStoresByMarketplace(stagingMarketplaceId ?? 0);
  const { data: driversData = [], isLoading: driversLoading } = useDrivers();
  const drivers = driversData as Paquer[];
  const { data: warehouse } = useWarehouse();
  const { data: driverInfo, isLoading: driverInfoLoading } =
    useDriverCollectionInfo(values.driverId);

  useEffect(() => {
    if (!warehouse) return;
    setFieldValue('deposit', {
      name: warehouse.name,
      address: warehouse.address,
      lat: warehouse.locationAddress.lat,
      lng: warehouse.locationAddress.lng,
    });
  }, [warehouse]);

  useEffect(() => {
    const driver = drivers.find((d) => d.id === values.driverId);
    setFieldValue(
      'driverName',
      driver ? `${driver.name} ${driver.lastName}` : '',
    );
    setFieldValue('driverDni', driver?.docNumber ?? '');
    setFieldValue('vehiclePlate', '');
  }, [values.driverId, drivers]);

  useEffect(() => {
    if (driverInfo?.lastVehiclePlate) {
      setFieldValue('vehiclePlate', driverInfo.lastVehiclePlate);
    }
  }, [driverInfo]);

  const stagingStore = stagingStores.find((s) => s.id === stagingStoreId);
  const stagingHasGeo = !!(stagingStore?.lat && stagingStore?.lng);

  const marketplaceOptions = marketplaces.map((m) => ({
    label: m.name,
    value: m.id,
  }));

  const stagingStoreOptions = stagingStores
    .filter((s) => !values.stores.some((added) => added.storeId === s.id))
    .map((s) => ({ label: `${s.storeCode} - ${s.name}`, value: s.id }));

  const driverOptions = drivers.map((d) => ({
    label: `${d.name} ${d.lastName}`,
    value: d.id,
  }));

  const filterOption = (input: string, option: any) =>
    (option?.label ?? option?.children ?? '')
      .toString()
      .toLowerCase()
      .includes(input.toLowerCase());

  const handleStagingMarketplaceChange = (value: number) => {
    setStagingMarketplaceId(value);
    setStagingStoreId(null);
  };

  const addStore = () => {
    if (!stagingStore || !stagingHasGeo) return;
    const marketplace = marketplaces.find((m) => m.id === stagingMarketplaceId);
    if (!marketplace) return;

    const newEntry: StoreEntry = {
      marketplaceId: marketplace.id,
      marketplaceName: marketplace.name,
      storeId: stagingStore.id,
      storeName: stagingStore.name,
      address: stagingStore.address ?? '',
      lat: stagingStore.lat as number,
      lng: stagingStore.lng as number,
    };

    setFieldValue('stores', [...values.stores, newEntry]);
    setStagingStoreId(null);
  };

  const removeStore = (storeId: number) => {
    setFieldValue(
      'stores',
      values.stores.filter((s) => s.storeId !== storeId),
    );
  };

  const handleNext = async () => {
    const fields = STEP_FIELDS[currentStep];
    fields.forEach((field) => setFieldTouched(field, true));
    const errs = await validateForm();
    const hasErrors = fields.some((field) => !!errs[field]);
    if (
      currentStep === 0 &&
      (driverInfoLoading || !driverInfo || driverInfo.hasActiveCollection)
    )
      return;
    if (!hasErrors) onNext();
  };

  const selectedDriver = drivers.find((d) => d.id === values.driverId);

  return (
    <>
      {currentStep === 0 && (
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Spin spinning={driversLoading}>
              <SelectField
                label="Driver"
                fieldName="driverId"
                options={driverOptions}
                showSearch
                filterOption={filterOption}
                allowClear
              />
            </Spin>
          </Col>
          {values.driverId && (
            <Col span={24}>
              {driverInfoLoading && (
                <Alert
                  type="info"
                  showIcon
                  icon={<Spin size="small" />}
                  message="Verificando disponibilidad del driver..."
                />
              )}
              {!driverInfoLoading && driverInfo?.hasActiveCollection && (
                <Alert
                  type="error"
                  showIcon
                  message="Driver no disponible"
                  description={`El driver tiene una colecta activa (ID: ${driverInfo.activeCollectionId}). Elegí otro driver.`}
                />
              )}
              {!driverInfoLoading &&
                driverInfo &&
                !driverInfo.hasActiveCollection && (
                  <Alert
                    type="success"
                    showIcon
                    message="Driver disponible"
                    description={
                      driverInfo.lastCollectionAt
                        ? `Última colecta: ${new Date(driverInfo.lastCollectionAt).toLocaleString('es-AR')}`
                        : undefined
                    }
                  />
                )}
            </Col>
          )}
          {selectedDriver &&
            !driverInfoLoading &&
            driverInfo &&
            !driverInfo.hasActiveCollection && (
              <>
                <Col span={24}>
                  <Card title="Información del driver">
                    <Descriptions column={2} size="small">
                      <Descriptions.Item label="Nombre">
                        {selectedDriver.name ?? '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Apellido">
                        {selectedDriver.lastName ?? '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="DNI">
                        {selectedDriver.docNumber ?? '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Email">
                        {selectedDriver.email ?? '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Teléfono">
                        {selectedDriver.mobile || selectedDriver.phone || '-'}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
                <Col span={24}>
                  <InputField
                    label="Dominio del vehículo"
                    fieldName="vehiclePlate"
                    placeholder="Ej: AA123BB"
                  />
                </Col>
              </>
            )}
        </Row>
      )}

      {currentStep === 1 && (
        <Row gutter={[16, 16]}>
          {touched.stores && typeof errors.stores === 'string' && (
            <Col span={24}>
              <Alert type="error" showIcon message={errors.stores} />
            </Col>
          )}

          {values.stores.length > 0 && (
            <Col span={24}>
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                {values.stores.map((store) => (
                  <Card
                    key={store.storeId}
                    title={
                      <Space>
                        <Tag color="blue">{store.marketplaceName}</Tag>
                        <Text strong>{store.storeName}</Text>
                      </Space>
                    }
                    extra={
                      <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => removeStore(store.storeId)}
                      >
                        Eliminar
                      </Button>
                    }
                  >
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={12}>
                        <Descriptions column={1} size="small">
                          <Descriptions.Item label="Dirección">
                            {store.address || '-'}
                          </Descriptions.Item>
                        </Descriptions>
                      </Col>
                      <Col xs={24} md={12}>
                        <div style={{ height: 120 }}>
                          <MapContainer
                            center={[store.lat, store.lng]}
                            zoom={15}
                            style={{ height: '100%', width: '100%' }}
                          >
                            <TileLayer
                              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <Marker
                              position={[store.lat, store.lng]}
                              icon={defaultMarkerIcon}
                            />
                          </MapContainer>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </Space>
            </Col>
          )}

          {values.stores.length > 0 && (
            <Col span={24}>
              <Divider style={{ margin: '4px 0' }} />
            </Col>
          )}

          <Col span={24}>
            <Card title="Agregar tienda">
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Spin spinning={marketplacesLoading}>
                    <div>
                      <Text
                        type="secondary"
                        style={{ display: 'block', marginBottom: 4 }}
                      >
                        Marketplace
                      </Text>
                      <Select
                        style={{ width: '100%' }}
                        options={marketplaceOptions}
                        value={stagingMarketplaceId}
                        onChange={handleStagingMarketplaceChange}
                        showSearch
                        filterOption={filterOption}
                        allowClear
                        onClear={() => {
                          setStagingMarketplaceId(null);
                          setStagingStoreId(null);
                        }}
                        placeholder="Seleccioná un marketplace"
                      />
                    </div>
                  </Spin>
                </Col>

                {stagingMarketplaceId && (
                  <Col span={24}>
                    <Spin spinning={stagingStoresLoading}>
                      <div>
                        <Text
                          type="secondary"
                          style={{ display: 'block', marginBottom: 4 }}
                        >
                          Tienda
                        </Text>
                        <Select
                          style={{ width: '100%' }}
                          options={stagingStoreOptions}
                          value={stagingStoreId}
                          onChange={setStagingStoreId}
                          showSearch
                          filterOption={filterOption}
                          allowClear
                          onClear={() => setStagingStoreId(null)}
                          placeholder="Seleccioná una tienda"
                        />
                      </div>
                    </Spin>
                  </Col>
                )}

                {stagingStore && (
                  <Col span={24}>
                    {!stagingHasGeo ? (
                      <Alert
                        type="error"
                        showIcon
                        message="Tienda sin geolocalización"
                        description="Esta tienda no tiene coordenadas registradas. Por favor, comunicate con un Operador Logístico para que actualice la información."
                      />
                    ) : (
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="Nombre">
                          {stagingStore.name}
                        </Descriptions.Item>
                        <Descriptions.Item label="Dirección">
                          {stagingStore.address || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Código">
                          {stagingStore.storeCode}
                        </Descriptions.Item>
                      </Descriptions>
                    )}
                  </Col>
                )}

                <Col
                  span={24}
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    onClick={addStore}
                    disabled={!stagingStore || !stagingHasGeo}
                  >
                    Agregar tienda
                  </Button>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      {currentStep === 2 && (
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card title="Configuración de notificaciones">
              <Space direction="vertical" size={20} style={{ width: '100%' }}>
                <Row align="middle" justify="space-between">
                  <Col>
                    <Text>¿Enviar notificación a las tiendas?</Text>
                  </Col>
                  <Col>
                    <Switch
                      checked={values.notifyStore}
                      onChange={(checked) =>
                        setFieldValue('notifyStore', checked)
                      }
                    />
                  </Col>
                </Row>
                <Row align="middle" justify="space-between">
                  <Col>
                    <Text>¿Enviar notificación al driver?</Text>
                  </Col>
                  <Col>
                    <Switch
                      checked={values.notifyDriver}
                      onChange={(checked) =>
                        setFieldValue('notifyDriver', checked)
                      }
                    />
                  </Col>
                </Row>
              </Space>
            </Card>
          </Col>
        </Row>
      )}

      <Row justify="end" style={{ marginTop: 24 }}>
        <Space>
          <Button htmlType="button" onClick={onClose}>
            Cancelar
          </Button>
          {currentStep > 0 && (
            <Button htmlType="button" onClick={onBack}>
              Atrás
            </Button>
          )}
          {currentStep < 2 ? (
            <Button type="primary" htmlType="button" onClick={handleNext}>
              Siguiente
            </Button>
          ) : (
            <Button
              type="primary"
              htmlType="button"
              loading={isSubmitting}
              onClick={submitForm}
            >
              Crear Colecta
            </Button>
          )}
        </Space>
      </Row>
    </>
  );
};

interface CreateCollectionModalProps {
  open: boolean;
  onClose: () => void;
}

const CreateCollectionModal: React.FC<CreateCollectionModalProps> = ({
  open,
  onClose,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { mutateAsync: createCollection } = useCreateCollection();

  const handleClose = () => {
    setCurrentStep(0);
    onClose();
  };

  const handleSubmit = async (values: CollectionFormValues) => {
    await createCollection(values as CreateCollectionPayload);
    handleClose();
  };

  const steps = [
    { title: 'Driver' },
    { title: 'Tiendas' },
    { title: 'Notificaciones' },
  ];

  return (
    <Modal
      title="Nueva Colecta"
      open={open}
      onCancel={handleClose}
      footer={null}
      width={720}
      destroyOnClose
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        <div>
          <Steps
            current={currentStep}
            items={steps}
            style={{ marginBottom: 24 }}
            size="small"
          />
          <FormContent
            currentStep={currentStep}
            onNext={() => setCurrentStep((prev) => prev + 1)}
            onBack={() => setCurrentStep((prev) => prev - 1)}
            onClose={handleClose}
          />
        </div>
      </Formik>
    </Modal>
  );
};

export default CreateCollectionModal;
